(function(root) {
  'use strict';

  var EXPECTED_QUANTITIES = [
    50, 100, 150, 200, 250, 300, 400, 500, 1000, 2000, 2500, 3000, 4000,
    5000, 6000, 7000, 8000, 9000, 10000, 12000, 15000, 20000, 25000, 40000,
    50000
  ];
  var EXPECTED_MATERIAL_FINISHES = {
    white_bopp: ['gloss', 'matte'],
    clear: ['gloss', 'matte'],
    metallic: ['gloss', 'matte'],
    holographic_metallic: ['gloss', 'matte'],
    semi_gloss_paper: ['no_lamination', 'gloss_laminate', 'matte_laminate'],
    kraft_paper: ['no_lamination', 'gloss_laminate', 'matte_laminate']
  };

  function roundCurrency(value) {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  function formatDimension(value) {
    return String(Math.round(Number(value) * 1000) / 1000);
  }

  function normalizeDimension(value, axis, dimensions) {
    var numeric = Number(value);
    var minimum = Number(axis === 'width' ? dimensions.minimum_width_in : dimensions.minimum_height_in);
    var maximum = Number(axis === 'width' ? dimensions.maximum_width_in : dimensions.maximum_height_in);
    var scale = Math.pow(10, Number(dimensions.decimal_places));
    if (!Number.isFinite(numeric) || numeric < minimum || numeric > maximum) return null;
    if (Math.abs(numeric * scale - Math.round(numeric * scale)) > 0.0000001) return null;
    return Math.round(numeric * scale) / scale;
  }

  function quantityBounds(quantities, quantity) {
    for (var index = 0; index < quantities.length; index += 1) {
      if (quantities[index] === quantity) return { low: quantity, high: quantity };
      if (quantities[index] > quantity) {
        return { low: quantities[index - 1], high: quantities[index] };
      }
    }
    return null;
  }

  function interpolateQuantityTotal(orderTotals, quantity) {
    var quantities = Object.keys(orderTotals).map(Number).sort(function(left, right) {
      return left - right;
    });
    var exact = Number(orderTotals[String(quantity)]);
    if (exact > 0) return exact;
    if (!quantities.length || quantity < quantities[0] || quantity > quantities[quantities.length - 1]) {
      return null;
    }
    var upperIndex = quantities.findIndex(function(value) { return value > quantity; });
    var low = quantities[upperIndex - 1];
    var high = quantities[upperIndex];
    var progress = (quantity - low) / (high - low);
    var unitLow = Number(orderTotals[String(low)]) / low;
    var unitHigh = Number(orderTotals[String(high)]) / high;
    return (unitLow + (unitHigh - unitLow) * progress) * quantity;
  }

  function estimateMarketTotal(curves, area, quantity) {
    var points = curves.map(function(curve) {
      return {
        area: curve.area_in2,
        total: interpolateQuantityTotal(curve.order_totals, quantity)
      };
    }).filter(function(point) {
      return point.total !== null;
    }).sort(function(left, right) {
      return left.area - right.area;
    });
    if (points.length < 2) return 0;

    var lower = points[0];
    var upper = points[1];
    if (area >= points[points.length - 1].area) {
      lower = points[points.length - 2];
      upper = points[points.length - 1];
    } else if (area > points[0].area) {
      var upperIndex = points.findIndex(function(point) { return point.area >= area; });
      lower = points[upperIndex - 1];
      upper = points[upperIndex];
    }
    var progress = (area - lower.area) / (upper.area - lower.area);
    return Math.max(0.01, lower.total + (upper.total - lower.total) * progress);
  }

  function pressFeet(contract, width, height, quantity) {
    var layout = contract.layout_model;
    var pitchAcross = width + layout.bleed_in * 2 + layout.horizontal_gap_in;
    var labelsAcross = Math.max(
      1,
      Math.floor((layout.printable_width_in + layout.horizontal_gap_in) / pitchAcross)
    );
    var frames = Math.ceil(quantity / labelsAcross);
    var pitchAround = height + layout.bleed_in * 2 + layout.vertical_gap_in;
    return frames * pitchAround / 12;
  }

  function resolveAnchorTotal(contract, width, height, requestedQuantity, model, baseModel) {
    var baseTotal = 0;
    var relationshipTotal = 0;
    for (var index = 0; index < contract.quantities.length; index += 1) {
      var quantity = contract.quantities[index];
      var marketTotal = estimateMarketTotal(
        contract.market_model.reference_curves,
        width * height,
        quantity
      );
      if (!(marketTotal > 0)) return 0;
      var rectangularFeet = pressFeet(contract, width, height, quantity);
      var squareSide = Math.sqrt(width * height);
      var squareFeet = pressFeet(contract, squareSide, squareSide, quantity);
      var geometryFactor =
        (rectangularFeet + baseModel.offset_press_feet) /
        (squareFeet + baseModel.offset_press_feet);
      var calibratedBase = roundCurrency(
        marketTotal * contract.market_model.target_multiplier * geometryFactor
      );
      baseTotal = Math.max(baseTotal, calibratedBase);

      var relationshipFactor =
        model.scale *
        (rectangularFeet + model.offset_press_feet) /
        (rectangularFeet + baseModel.offset_press_feet);
      relationshipTotal = Math.max(
        relationshipTotal,
        roundCurrency(baseTotal * relationshipFactor)
      );
      if (quantity === requestedQuantity) return relationshipTotal;
    }
    return 0;
  }

  function resolveCompactShopifyPrice(contract, request) {
    if (!contract || contract.status !== 'approved' ||
        contract.mode !== 'market_calibrated_parametric_v1') return null;
    var width = normalizeDimension(request.width, 'width', contract.dimensions);
    var height = normalizeDimension(request.height, 'height', contract.dimensions);
    var quantity = Number(request.quantity);
    if (width === null || height === null || !Number.isInteger(quantity)) return null;

    var quantities = contract.quantities;
    if (!quantities.length || quantity < quantities[0] ||
        quantity > quantities[quantities.length - 1]) return null;
    var combination = contract.combinations.find(function(item) {
      return item.material === request.material && item.finish === request.finish;
    });
    var model = combination && contract.relationship_models[combination.model_key];
    var baseModel = contract.relationship_models[contract.base_model_key];
    var rushMultiplier = Number(contract.rush_multipliers[request.turnaround]);
    if (!combination || !model || !baseModel || !(rushMultiplier > 0)) return null;

    var bounds = quantityBounds(quantities, quantity);
    if (!bounds) return null;
    var lowTotal = resolveAnchorTotal(contract, width, height, bounds.low, model, baseModel);
    var highTotal = resolveAnchorTotal(contract, width, height, bounds.high, model, baseModel);
    if (!(lowTotal > 0) || !(highTotal > 0)) return null;

    var regularTotal = lowTotal;
    if (bounds.low !== bounds.high) {
      var progress = (quantity - bounds.low) / (bounds.high - bounds.low);
      var unitLow = lowTotal / bounds.low;
      var unitHigh = highTotal / bounds.high;
      regularTotal = roundCurrency((unitLow + (unitHigh - unitLow) * progress) * quantity);
    }
    var total = roundCurrency(regularTotal * rushMultiplier);
    return {
      total: total,
      regular_total: regularTotal,
      unit_price: total / quantity,
      quantity: quantity,
      q_low: bounds.low,
      q_high: bounds.high,
      width_in: width,
      height_in: height,
      material: request.material,
      finish: request.finish,
      turnaround: request.turnaround,
      rush_multiplier: rushMultiplier
    };
  }

  function create(config) {
    var validation = validate(config);
    var dimensions = validation.valid ? config.dimensions : {};
    var quantities = validation.valid ? config.quantities.map(Number) : [];

    function resolve(request) {
      if (!validation.valid || !request) return null;
      var result = resolveCompactShopifyPrice(config, request);
      if (!result) return null;
      var sizeKey = formatDimension(result.width_in) + 'x' + formatDimension(result.height_in);
      return {
        version: 'roll_labels_parametric_v2',
        pricing_table_version: config.pricing_table_version,
        width_in: result.width_in,
        height_in: result.height_in,
        area_in2: Math.round(result.width_in * result.height_in * 1000000) / 1000000,
        size_key: sizeKey,
        size_tier_in2: sizeKey,
        qty_entered: result.quantity,
        q_low: result.q_low,
        q_high: result.q_high,
        u_interp: result.regular_total / result.quantity,
        u_final: result.unit_price,
        global_base_scalar: 1,
        regular_total: result.regular_total,
        total_desired: result.total,
        unit_price: result.unit_price,
        material_key: result.material,
        material_mult: 1,
        finish_key: result.finish,
        rush_option: result.turnaround,
        rush_mult: result.rush_multiplier,
        quantity: result.quantity,
        cart_mode: 'product_single',
        order_adjustment_cents: 0
      };
    }

    return {
      validation: validation,
      normalizeDimension: function(value, axis) {
        return validation.valid ? normalizeDimension(value, axis, dimensions) : null;
      },
      findQuantityBounds: function(quantity) {
        return validation.valid ? quantityBounds(quantities, Number(quantity)) : null;
      },
      resolve: resolve
    };
  }

  function validate(config) {
    var errors = [];
    if (!config || config.schema_version !== 2) errors.push('schema version 2');
    if (!config || config.family !== 'roll_labels') errors.push('roll_labels family');
    if (!config || config.status !== 'approved') errors.push('approved status');
    if (!config || config.currency !== 'USD') errors.push('USD currency');
    if (!config || config.mode !== 'market_calibrated_parametric_v1') {
      errors.push('approved parametric pricing model');
    }
    if (!config || config.price_source !== 'market_calibrated_customer_sell_price') {
      errors.push('approved market-calibrated customer sell price source');
    }
    if (!config || config.pricing_format !== 'order_totals') errors.push('order totals pricing format');
    if (!config || !config.pricing_table_version) errors.push('pricing table version');
    if (!config || config.machine_applied_fee_included !== false) {
      errors.push('external Machine Applied fee');
    }

    var dimensions = config && config.dimensions;
    if (!dimensions ||
        Number(dimensions.minimum_width_in) !== 0.5 ||
        Number(dimensions.maximum_width_in) !== 12 ||
        Number(dimensions.minimum_height_in) !== 0.5 ||
        Number(dimensions.maximum_height_in) !== 12 ||
        Number(dimensions.decimal_places) !== 3 ||
        dimensions.increment_in !== null ||
        Number(dimensions.maximum_printable_area_in2) !== 144 ||
        dimensions.shape_changes_price !== false ||
        dimensions.area_alone_preserves_engine_price !== false) {
      errors.push('approved precision dimension contract');
    }

    var quantities = config && Array.isArray(config.quantities) ? config.quantities.map(Number) : [];
    if (quantities.length !== EXPECTED_QUANTITIES.length ||
        quantities.some(function(value, index) { return value !== EXPECTED_QUANTITIES[index]; })) {
      errors.push('approved quantity anchors');
    }

    var rush = config && config.rush_multipliers;
    if (!rush || Number(rush.regular) !== 1 ||
        Number(rush.rush_3_5) !== 1.2 ||
        Number(rush.rush_1_2) !== 1.5) {
      errors.push('approved turnaround multipliers');
    }

    var market = config && config.market_model;
    if (!market || Number(market.target_multiplier) !== 0.96 ||
        !Array.isArray(market.reference_curves) || market.reference_curves.length !== 4) {
      errors.push('approved market model');
    } else if (market.reference_curves.some(function(curve) {
      return !(Number(curve.area_in2) > 0) ||
        !curve.order_totals ||
        Object.keys(curve.order_totals).length < 20 ||
        Object.keys(curve.order_totals).some(function(quantity) {
          return !(Number(curve.order_totals[quantity]) > 0);
        });
    })) {
      errors.push('complete market reference curves');
    }
    var layout = config && config.layout_model;
    if (!layout || Number(layout.printable_width_in) !== 12.6 ||
        Number(layout.bleed_in) !== 0.0625 ||
        Number(layout.horizontal_gap_in) !== 0.125 ||
        Number(layout.vertical_gap_in) !== 0.125) {
      errors.push('approved layout model');
    }

    var baseRecipe = config && config.base_recipe;
    if (!baseRecipe ||
        baseRecipe.key !== 'semi_gloss_unlaminated' ||
        baseRecipe.engine_material_id !== '55-semi-gloss-paper' ||
        baseRecipe.engine_laminate_id !== 'none' ||
        config.base_model_key !== 'semi_gloss_paper_no_lamination') {
      errors.push('approved base recipe and relationship model');
    }
    var relationshipModels = config && config.relationship_models;
    var requiredModelKeys = [
      'white_bopp_gloss',
      'clear_gloss',
      'metallic_gloss',
      'semi_gloss_paper_no_lamination',
      'semi_gloss_paper_gloss_laminate',
      'kraft_paper_no_lamination',
      'kraft_paper_gloss_laminate'
    ];
    if (!relationshipModels || requiredModelKeys.some(function(key) {
      var model = relationshipModels[key];
      return !model || !(Number(model.scale) > 0) || !(Number(model.offset_press_feet) > 0);
    })) {
      errors.push('complete relationship models');
    }

    var approvals = config && config.recipe_mapping_approvals;
    if (!approvals ||
        !approvals.white_bopp || approvals.white_bopp.status !== 'approved' ||
        approvals.white_bopp.engine_material_id !== 'pearl-white-bopp' ||
        !approvals.kraft_paper || approvals.kraft_paper.status !== 'approved' ||
        approvals.kraft_paper.engine_material_id !== 'material-1784333252510') {
      errors.push('approved material mappings');
    }

    var expectedCombinationKeys = [];
    Object.keys(EXPECTED_MATERIAL_FINISHES).forEach(function(material) {
      EXPECTED_MATERIAL_FINISHES[material].forEach(function(finish) {
        expectedCombinationKeys.push(material + '/' + finish);
      });
    });
    var combinationKeys = config && Array.isArray(config.combinations)
      ? config.combinations.map(function(combination) {
          return String(combination.material || '') + '/' + String(combination.finish || '');
        }).sort()
      : [];
    expectedCombinationKeys.sort();
    if (combinationKeys.length !== expectedCombinationKeys.length ||
        combinationKeys.some(function(key, index) { return key !== expectedCombinationKeys[index]; })) {
      errors.push('approved 14 material and finish combinations');
    } else if (config.combinations.some(function(combination) {
      return !relationshipModels ||
        !relationshipModels[combination.model_key] ||
        !combination.engine_material_id ||
        !combination.engine_laminate_id;
    })) {
      errors.push('complete combination mappings');
    }

    var validation = config && config.validation;
    var tolerance = validation && validation.tolerance;
    if (!validation || validation.passed !== true ||
        Number(validation.point_count) !== 292530 ||
        !tolerance ||
        Number(tolerance.maximum_percentage_error) !== 3 ||
        Number(tolerance.maximum_dollar_error) !== 5 ||
        tolerance.failure_rule !== 'percentage_and_dollar' ||
        !(Number(validation.maximum_percentage_error) <= Number(tolerance.maximum_percentage_error)) ||
        !(Number(validation.maximum_dollar_error) <= Number(tolerance.maximum_dollar_error))) {
      errors.push('approved validation report');
    }

    return {
      valid: errors.length === 0,
      errors: errors,
      point_count: validation && validation.point_count || 0,
      combination_count: combinationKeys.length,
      size_count: null,
      quantity_count: quantities.length
    };
  }

  root.ZUG_ROLL_LABEL_PRICING_ENGINE = {
    create: create,
    validate: validate,
    resolveCompactShopifyPrice: resolveCompactShopifyPrice
  };
})(typeof window !== 'undefined' ? window : this);
